import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useOutlet } from "react-router-dom";

import { styled } from "@mui/material/styles";
import { SideNav } from "./side-nav";
import { TopNav } from "./top-nav";
import { useSelector } from "react-redux";
import { selectAuth, selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const SIDE_NAV_WIDTH = 280;

const LayoutRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: SIDE_NAV_WIDTH,
  },
}));

const LayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

const ProtectedLayout = () => {
  const auth = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const outlet = useOutlet();

  let { pathname } = useLocation();
  const [openNav, setOpenNav] = useState(false);

  const handlePathnameChange = useCallback(() => {
    if (openNav) {
      setOpenNav(false);
    }
  }, [openNav]);

  useEffect(
    () => {
      handlePathnameChange();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname]
  );

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Role-based access control: Only admin and super_admin can access admin panel
  const allowedRoles = [Roles.ADMIN, Roles.SUPER_ADMIN];
  if (!user?.role || !allowedRoles.includes(user.role)) {
    // If user is authenticated but doesn't have the right role, log them out and redirect
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <TopNav onNavOpen={() => setOpenNav(true)} />
      <SideNav onClose={() => setOpenNav(false)} open={openNav} />
      <LayoutRoot>
        <LayoutContainer>{outlet}</LayoutContainer>
      </LayoutRoot>
    </>
  );
};

export default ProtectedLayout;
